import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, DatePicker } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import {
  CheckOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  useGenerateEstablishmentReport,
  usePreviewEstablishmentReport,
} from "../../../../../hooks/useReports";
import {
  printEstablishmentReport,
  type EstablishmentReport,
  type UnreportedUser,
} from "../../../../../api/reports";
import { EstablishmentReportPreviewModal } from "./EstablishmentReportPreviewModal";
import { PrintPosConfirmModal } from "./PrintPosConfirmModal";
import {
  getReportModalWidth,
  getUserListColumns,
  userListGridClass,
  userListMaxHeightClass,
  userListScrollClass,
} from "./userListLayout";
import dayjs, { Dayjs } from "dayjs";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { getZReport } from "../../../../../api/operations";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { usePosPrintersExist } from "../../../../../hooks/useDevices";
import { useAppSelector } from "../../../../../store/hooks";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

interface GenerateEstablishmentReportModalProps {
  open: boolean;
  onClose: () => void;
}

export const GenerateEstablishmentReportModal = ({
  open,
  onClose,
}: GenerateEstablishmentReportModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  // Продаваемото е асортимент, а в режим „със склад" — рецепта. Същото
  // разклонение като `useAssortmentReportTitleKey` в справките.
  const usesWarehouse = useAppSelector(
    (s) => s.auth.user?.uses_warehouse === true,
  );
  const previewMutation = usePreviewEstablishmentReport();
  const generateMutation = useGenerateEstablishmentReport();
  const message = useOrderingMessage();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  // Секцията с продаденото е по избор и по подразбиране е ИЗКЛЮЧЕНА. Изборът
  // важи за самия документ: при изключен чекбокс BE-то не смята и не записва
  // секцията, значи отчетът я няма и после в Справки, и на POS билета.
  const [includeSoldSellables, setIncludeSoldSellables] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // Сервитьорите с неприключени лични отчети — „Нулирай" ще ги издаде
  // автоматично, затова секцията ги показва предварително. Идват от preview-то.
  const [unreportedUsers, setUnreportedUsers] = useState<UnreportedUser[]>([]);
  const hasLoadedFirstDateRef = useRef(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showZReportConfirm, setShowZReportConfirm] = useState(false);
  const [isCallingZReport, setIsCallingZReport] = useState(false);
  const [showPrintPosConfirm, setShowPrintPosConfirm] = useState(false);
  const [isPrintingPos, setIsPrintingPos] = useState(false);
  const [lastReportId, setLastReportId] = useState<number | null>(null);
  // Ако заведението няма нито един POS принтер, въпросът „Печат от POS
  // принтер?" се прескача и се минава направо към Z-отчета. При неуспешна
  // заявка (default true) питаме както досега — безопасният вариант.
  const { data: posPrintersExist = true } = usePosPrintersExist(open);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedReport, setGeneratedReport] =
    useState<EstablishmentReport | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedDate(dayjs());
      setIncludeSoldSellables(false);
      setIsGenerating(false);
      setUnreportedUsers([]);
      hasLoadedFirstDateRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    const loadFirstReportDate = async () => {
      if (!open || hasLoadedFirstDateRef.current) return;
      try {
        const report = await previewMutation.mutateAsync({
          report_date: dayjs().toISOString(),
        });
        if (report.first_order_time) {
          setSelectedDate(dayjs(report.first_order_time));
        } else {
          setSelectedDate(dayjs());
        }
        setUnreportedUsers(report.unreported_users ?? []);
        hasLoadedFirstDateRef.current = true;
      } catch (error) {
        console.error("Error loading first report date:", error);
        setSelectedDate(dayjs());
        setUnreportedUsers([]);
      }
    };
    loadFirstReportDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPreview || showConfirm) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (!isGenerating) handlePreviewReport();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isGenerating, showPreview, showConfirm, onClose, isDesktop]);

  const handlePreviewReport = async () => {
    setIsGenerating(true);
    try {
      const report = await previewMutation.mutateAsync({
        report_date: selectedDate.toISOString(),
        include_sold_sellables: includeSoldSellables,
      });
      setUnreportedUsers(report.unreported_users ?? []);
      setGeneratedReport(report as unknown as EstablishmentReport);
      setIsPreviewMode(true);
      setShowPreview(true);
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(
        error,
        t("generateReport.establishmentPreviewError"),
      );
      message.error(errorMessage);
      console.error("Error previewing establishment report:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const showConfirmation = () => {
    setShowPreview(false);
    setShowConfirm(true);
  };

  const handleConfirmOk = async () => {
    setShowConfirm(false);
    await handleGenerateReport();
  };

  const handleZReportYes = async () => {
    setIsCallingZReport(true);
    try {
      // Endpoint-ът връща 200 и при отказал принтер — изходът е в status/error
      // (както в OperationsModal); без проверката отказът минаваше за успех.
      const result = await getZReport();
      if (result.status === "timeout") {
        notifyPrinterTimeout(result.error || t("operations.error"));
      } else if (result.status && result.status !== "success") {
        notifyPrinterError(result.error || t("operations.error"));
      } else {
        message.success(t("generateReport.zReportConfirmTitle"));
      }
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(
        error,
        t("generateReport.zReportConfirmTitle"),
      );
      notifyPrinterError(errorMessage);
      console.error("Error calling z-report:", error);
    } finally {
      setIsCallingZReport(false);
      setShowZReportConfirm(false);
      onClose();
    }
  };

  const handleZReportNo = () => {
    setShowZReportConfirm(false);
    onClose();
  };

  const handlePrintPosNo = () => {
    setShowPrintPosConfirm(false);
    setShowZReportConfirm(true);
  };

  const handlePrintPosYes = async () => {
    if (lastReportId == null) {
      setShowPrintPosConfirm(false);
      setShowZReportConfirm(true);
      return;
    }
    setIsPrintingPos(true);
    try {
      await printEstablishmentReport(lastReportId);
      message.success(t("establishmentReport.printPosSuccess"));
    } catch (error: unknown) {
      notifyPrinterError(
        getApiErrorMessage(error, t("establishmentReport.printPosError")),
      );
    } finally {
      setIsPrintingPos(false);
      setShowPrintPosConfirm(false);
      setShowZReportConfirm(true);
    }
  };

  const handleConfirmCancel = () => setShowConfirm(false);

  const handleGenerateReport = async () => {
    setIsCreatingReport(true);
    try {
      const created = await generateMutation.mutateAsync({
        report_date: selectedDate.toISOString(),
        include_sold_sellables: includeSoldSellables,
      });
      setGeneratedReport(null);
      setIsPreviewMode(false);
      setLastReportId(typeof created?.id === "number" ? created.id : null);
      // Нулирането вече отчита и сметки директно (автоматичните лични отчети)
      // — без инвалидацията закритите нефискализирани сметки остават на екрана
      // с масите до рефреш. Същите ключове като при личния отчет.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["bills"] }),
      ]);
      message.success(t("generateReport.establishmentReportCreated"));
      if (posPrintersExist) {
        setShowPrintPosConfirm(true);
      } else {
        setShowZReportConfirm(true);
      }
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(
        error,
        t("generateReport.establishmentGenerateError"),
      );
      message.error(errorMessage);
      console.error("Error generating establishment report:", error);
    } finally {
      setIsCreatingReport(false);
    }
  };

  const handlePreviewClose = () => {
    setShowPreview(false);
    setGeneratedReport(null);
    setIsPreviewMode(false);
    onClose();
  };

  // При много сервитьори с неприключени отчети списъкът минава в 2/3 колони
  // и модалът се разширява с тях (userListLayout.ts).
  const userListColumns = getUserListColumns(unreportedUsers.length, isNarrow);
  const width = getReportModalWidth(userListColumns, isMobile, isTablet);
  const bodyPad = isNarrow ? "14px 16px" : "18px 24px";

  return (
    <>
      {/* Z-Report Confirmation */}
      <Modal
        open={showZReportConfirm}
        onCancel={handleZReportNo}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={isMobile ? undefined : 420}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
        zIndex={2500}
      >
        <div
          style={{ background: R.surface, fontFamily: R.font, color: R.text }}
        >
          <ModalHeader
            icon={<ExclamationCircleOutlined />}
            title={t("generateReport.zReportConfirmTitle")}
            subtitle={t("generateReport.zReportConfirmDescription")}
            onClose={handleZReportNo}
            isNarrow={isNarrow}
          />
          <ModalFooter isNarrow={isNarrow} narrowFirst>
            <GhostButton
              label={t("common.no")}
              onClick={handleZReportNo}
              disabled={isCallingZReport}
            />
            <PrimaryButton
              label={t("common.yes")}
              onClick={handleZReportYes}
              loading={isCallingZReport}
            />
          </ModalFooter>
        </div>
      </Modal>

      <PrintPosConfirmModal
        open={showPrintPosConfirm}
        onYes={handlePrintPosYes}
        onNo={handlePrintPosNo}
        loading={isPrintingPos}
      />

      {/* Reset confirmation */}
      <Modal
        open={showConfirm}
        onCancel={handleConfirmCancel}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={isMobile ? undefined : 420}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
        zIndex={2000}
      >
        <div
          style={{ background: R.surface, fontFamily: R.font, color: R.text }}
        >
          <ModalHeader
            icon={<ExclamationCircleOutlined />}
            title={t("generateReport.resetReport")}
            subtitle={t(
              // С неприключени лични отчети „Нулирай" издава първо тях —
              // текстът предупреждава за двете действия наведнъж.
              unreportedUsers.length > 0
                ? "generateReport.resetConfirmEstablishmentWithPersonalDescription"
                : "generateReport.resetConfirmEstablishmentDescription",
            )}
            onClose={handleConfirmCancel}
            isNarrow={isNarrow}
          />
          <ModalFooter isNarrow={isNarrow} narrowFirst>
            <GhostButton
              label={t("common.cancel")}
              onClick={handleConfirmCancel}
            />
            <PrimaryButton
              label={t("generateReport.resetButton")}
              onClick={handleConfirmOk}
              loading={isCreatingReport}
            />
          </ModalFooter>
        </div>
      </Modal>

      <EstablishmentReportPreviewModal
        open={showPreview}
        onClose={handlePreviewClose}
        report={generatedReport}
        isPreviewMode={isPreviewMode}
        onConfirmCreate={showConfirmation}
        isCreating={isCreatingReport}
      />

      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={width}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div
          style={{ background: R.surface, fontFamily: R.font, color: R.text }}
        >
          <ModalHeader
            icon={<ShopOutlined />}
            title={t("generateReport.establishmentTitle")}
            onClose={onClose}
            isNarrow={isNarrow}
          />

          <div
            style={{
              padding: bodyPad,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  color: R.textMuted,
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                {t("generateReport.reportDate")}
              </div>
              <DatePicker
                value={selectedDate}
                onChange={(date) => date && setSelectedDate(date)}
                format="DD.MM.YYYY"
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 10,
                  borderColor: R.border,
                }}
                placeholder={t("generateReport.selectDate")}
                disabledDate={(current) =>
                  current && current.isAfter(dayjs(), "day")
                }
              />
            </div>

            {/* Сервитьорите с неприключени лични отчети — „Нулирай" ще издаде
                отчетите на всички изброени, накуп, без POS бележки. Същите
                редове като в модала за личен отчет, но без избор. */}
            {unreportedUsers.length > 0 && (
              <div>
                <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">
                  {t(
                    unreportedUsers.length === 1
                      ? "generateReport.unreportedUsersTitleSingle"
                      : "generateReport.unreportedUsersTitle",
                  )}
                </div>
                <div
                  className={`grid gap-1.5 ${userListScrollClass} ${userListMaxHeightClass} ${userListGridClass[userListColumns]}`}
                >
                  {unreportedUsers.map((unreportedUser) => (
                    <div
                      key={unreportedUser.user_id}
                      className="flex items-center justify-between gap-2 rounded-[10px] border border-pos-line2 bg-pos-card px-3 py-2.5"
                    >
                      <span className="inline-flex min-w-0 flex-1 items-center gap-2">
                        <UserOutlined className="flex-shrink-0 text-[13px] text-pos-muted" />
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13.5px] font-semibold text-pos-ink">
                          {unreportedUser.user_name}
                        </span>
                      </span>
                      <span className="flex-shrink-0 rounded-full border border-pos-accent/25 bg-pos-ready-bg px-2 py-[3px] font-pos-mono text-[13px] font-extrabold tracking-[-0.2px] text-pos-accent-ink">
                        {Number(unreportedUser.unreported_amount).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Собствен чекбокс, а не antd — antd рисува своя в синьо, а
                „Маркиране" е изцяло в оранжевото `pos-accent`. Същият квадрат
                като при добавките в модала „Промени". */}
            <button
              type="button"
              onClick={() => setIncludeSoldSellables((prev) => !prev)}
              aria-pressed={includeSoldSellables}
              className="flex cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0 text-left text-[13px] text-pos-ink"
            >
              <span
                className={`grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-[7px] border-2 ${
                  includeSoldSellables
                    ? "border-pos-accent bg-pos-accent text-white"
                    : "border-pos-check bg-pos-card"
                }`}
              >
                {includeSoldSellables && (
                  <CheckOutlined className="text-[12px]" />
                )}
              </span>
              {t(
                usesWarehouse
                  ? "generateReport.showSoldRecipes"
                  : "generateReport.showSoldAssortments",
              )}
            </button>
          </div>

          <ModalFooter isNarrow={isNarrow} narrowFirst>
            <GhostButton
              label={t("common.cancel")}
              onClick={onClose}
              disabled={isGenerating}
              shortcut={isDesktop ? "Esc" : undefined}
            />
            <PrimaryButton
              label={t("generateReport.previewReport")}
              onClick={handlePreviewReport}
              loading={isGenerating}
              shortcut={isDesktop ? "⏎" : undefined}
            />
          </ModalFooter>
        </div>
      </Modal>
    </>
  );
};
